/**
 * Users admin page.
 *
 * Lets admins:
 *   - see all users (active + inactive)
 *   - add a new user
 *   - edit role / name / phone / active flag
 *   - reset a user's password
 *   - deactivate a user
 *
 * All routes gated server-side by requireAdmin(); this page also hides itself
 * from non-admins in the sidebar and shows a "no access" message just in case.
 */
import { useMemo, useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import {
  Users as UsersIcon,
  UserPlus,
  Loader2,
  KeyRound,
  Pencil,
  Trash2,
  ShieldCheck,
  Check,
  AlertTriangle,
  Copy,
} from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useAuth } from "@/hooks/useAuth"
import {
  usersApi,
  ApiError,
  type AdminUser,
  type Role,
  type CreateUserInput,
  type UpdateUserInput,
} from "@/lib/api"

const ROLES: { value: Role; label: string; hint: string }[] = [
  { value: "admin", label: "Admin", hint: "Full access — manage users, settings, integrations." },
  { value: "manager", label: "Manager", hint: "Can import data + see all deals; no user admin." },
  { value: "broker", label: "Broker", hint: "Owns leads and deals. Standard broker role." },
  { value: "assistant", label: "Assistant", hint: "Supports brokers; limited edit rights." },
  { value: "compliance", label: "Compliance", hint: "Read-only oversight of records + audit log." },
]

function roleLabel(r: Role): string {
  return ROLES.find((x) => x.value === r)?.label ?? r
}

function formatDate(ts: number | null): string {
  if (!ts) return "—"
  return new Date(ts * 1000).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "Australia/Brisbane",
  })
}

export default function UsersPage() {
  const { user } = useAuth()
  const isAdmin = user?.role === "admin"

  if (!isAdmin) {
    return (
      <AppShell title="Users" subtitle="Team access & permissions">
        <Card className="max-w-xl border-amber-200 bg-amber-50 p-4">
          <div className="flex gap-3 text-sm text-amber-900">
            <AlertTriangle className="h-5 w-5 shrink-0" />
            <div>Only admins can manage users. Ask your admin to change roles or reset passwords.</div>
          </div>
        </Card>
      </AppShell>
    )
  }

  return <UsersAdmin />
}

// ============================================================
// Main admin view
// ============================================================
function UsersAdmin() {
  const { user: me } = useAuth()
  const qc = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [editUser, setEditUser] = useState<AdminUser | null>(null)
  const [resetUser, setResetUser] = useState<AdminUser | null>(null)
  const [deactivateUser, setDeactivateUser] = useState<AdminUser | null>(null)
  const [flash, setFlash] = useState<string | null>(null)

  const list = useQuery({
    queryKey: ["users-admin"],
    queryFn: () => usersApi.listAll(),
  })

  const users = useMemo(() => list.data?.users ?? [], [list.data])
  const active = users.filter((u) => u.isActive)
  const inactive = users.filter((u) => !u.isActive)

  const flashOk = (msg: string) => {
    setFlash(msg)
    setTimeout(() => setFlash(null), 3500)
  }

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["users-admin"] })
    qc.invalidateQueries({ queryKey: ["users"] })
  }

  return (
    <AppShell
      title="Users"
      subtitle="Team access & permissions"
      actions={
        <Button onClick={() => setShowCreate(true)}>
          <UserPlus className="mr-2 h-4 w-4" />
          Add user
        </Button>
      }
    >
      <div className="max-w-5xl space-y-6">
        {flash && (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
            <Check className="mr-1 inline h-4 w-4" />
            {flash}
          </div>
        )}

        <Card className="p-0 overflow-hidden">
          <div className="border-b border-border bg-rc-grey-50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Active users ({active.length})
          </div>
          {list.isLoading ? (
            <div className="p-6 text-center text-muted-foreground">
              <Loader2 className="mx-auto h-5 w-5 animate-spin" />
            </div>
          ) : active.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">No active users.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs uppercase text-muted-foreground">
                <tr className="border-b border-border">
                  <th className="px-4 py-2 font-medium">Name</th>
                  <th className="px-4 py-2 font-medium">Email</th>
                  <th className="px-4 py-2 font-medium">Role</th>
                  <th className="px-4 py-2 font-medium">2FA</th>
                  <th className="px-4 py-2 font-medium">Last login</th>
                  <th className="px-4 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {active.map((u) => (
                  <tr key={u.id} className="border-b border-border last:border-b-0 hover:bg-rc-grey-50">
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-foreground">
                        {u.fullName}
                        {u.id === me?.id && (
                          <span className="ml-2 text-xs text-muted-foreground">(you)</span>
                        )}
                      </div>
                      {u.phone && (
                        <div className="text-xs text-muted-foreground">{u.phone}</div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">{u.email}</td>
                    <td className="px-4 py-2.5">
                      <RolePill role={u.role} />
                    </td>
                    <td className="px-4 py-2.5">
                      {u.totpConfirmedAt ? (
                        <Badge variant="success" className="text-xs">
                          <ShieldCheck className="mr-1 h-3 w-3" />
                          Enrolled
                        </Badge>
                      ) : (
                        <Badge variant="muted" className="text-xs">
                          Not enrolled
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-muted-foreground">{formatDate(u.lastLoginAt)}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setEditUser(u)} title="Edit">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setResetUser(u)}
                          title="Reset password"
                        >
                          <KeyRound className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeactivateUser(u)}
                          disabled={u.id === me?.id}
                          title={u.id === me?.id ? "You can't deactivate yourself" : "Deactivate"}
                          className="hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        {inactive.length > 0 && (
          <Card className="p-0 overflow-hidden">
            <div className="border-b border-border bg-rc-grey-50 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Deactivated ({inactive.length})
            </div>
            <table className="w-full text-sm">
              <tbody>
                {inactive.map((u) => (
                  <tr key={u.id} className="border-b border-border last:border-b-0">
                    <td className="px-4 py-2.5">
                      <div className="font-medium text-muted-foreground">{u.fullName}</div>
                      <div className="text-xs text-muted-foreground">{u.email}</div>
                    </td>
                    <td className="px-4 py-2.5">
                      <RolePill role={u.role} muted />
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <Button variant="ghost" size="sm" onClick={() => setEditUser(u)}>
                        Reactivate
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        <Card className="border-blue-200 bg-blue-50 p-4">
          <div className="flex gap-3 text-sm text-blue-900">
            <UsersIcon className="h-5 w-5 shrink-0" />
            <div>
              <div className="font-medium">How this works</div>
              <ul className="mt-1 list-disc space-y-0.5 pl-4 text-blue-800/90">
                <li>
                  New users log in with the password you set, then are prompted to enrol their
                  authenticator app on first sign-in.
                </li>
                <li>
                  Password resets take effect immediately. Share the temp password securely and ask
                  the user to change it under <em>My Settings</em>.
                </li>
                <li>
                  "Clear 2FA" on reset forces the user to re-enrol their authenticator — use this if
                  they've lost their device.
                </li>
              </ul>
            </div>
          </div>
        </Card>
      </div>

      {/* Modals */}
      <CreateUserDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        onCreated={(email, tempPassword) => {
          setShowCreate(false)
          refresh()
          flashOk(`User ${email} created. Temporary password: ${tempPassword}`)
        }}
      />
      {editUser && (
        <EditUserDialog
          user={editUser}
          isSelf={editUser.id === me?.id}
          onClose={() => setEditUser(null)}
          onSaved={() => {
            setEditUser(null)
            refresh()
            flashOk("User updated.")
          }}
        />
      )}
      {resetUser && (
        <ResetPasswordDialog
          user={resetUser}
          onClose={() => setResetUser(null)}
          onDone={(tempPassword) => {
            setResetUser(null)
            refresh()
            flashOk(`Password reset. Temporary password: ${tempPassword}`)
          }}
        />
      )}
      {deactivateUser && (
        <DeactivateDialog
          user={deactivateUser}
          onClose={() => setDeactivateUser(null)}
          onDone={() => {
            setDeactivateUser(null)
            refresh()
            flashOk("User deactivated.")
          }}
        />
      )}
    </AppShell>
  )
}

// ============================================================
// Role pill
// ============================================================
function RolePill({ role, muted }: { role: Role; muted?: boolean }) {
  const variant =
    role === "admin"
      ? "success"
      : role === "manager"
        ? "muted"
        : role === "broker"
          ? "muted"
          : "muted"
  return (
    <Badge variant={muted ? "muted" : (variant as "success" | "muted")} className="text-xs">
      {roleLabel(role)}
    </Badge>
  )
}

// ============================================================
// Random password helper
// ============================================================
function suggestPassword(): string {
  // 16-char URL-safe token, guaranteed ≥12 and mixed alphanum.
  const alpha = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789"
  const bytes = crypto.getRandomValues(new Uint8Array(16))
  let out = ""
  for (const b of bytes) out += alpha[b % alpha.length]
  return out
}

function CopyableField({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="flex items-center gap-2">
      <code className="flex-1 rounded-md border border-border bg-rc-grey-50 px-3 py-2 font-mono text-sm">
        {value}
      </code>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => {
          void navigator.clipboard.writeText(value)
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        }}
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
  )
}

// ============================================================
// Create user dialog
// ============================================================
function CreateUserDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated: (email: string, tempPassword: string) => void
}) {
  const [email, setEmail] = useState("")
  const [fullName, setFullName] = useState("")
  const [phone, setPhone] = useState("")
  const [role, setRole] = useState<Role>("broker")
  const [password, setPassword] = useState(() => suggestPassword())
  const [error, setError] = useState<string | null>(null)

  const reset = () => {
    setEmail("")
    setFullName("")
    setPhone("")
    setRole("broker")
    setPassword(suggestPassword())
    setError(null)
  }

  const mutation = useMutation({
    mutationFn: (input: CreateUserInput) => usersApi.create(input),
    onSuccess: () => {
      const tempPw = password
      const targetEmail = email
      reset()
      onCreated(targetEmail, tempPw)
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Failed to create user"),
  })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (password.length < 12) {
      setError("Password must be at least 12 characters.")
      return
    }
    mutation.mutate({
      email: email.trim(),
      fullName: fullName.trim(),
      phone: phone.trim() || null,
      role,
      password,
    })
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          reset()
          onClose()
        }
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add user</DialogTitle>
          <DialogDescription>
            Creates a new user. They'll be prompted to enrol their authenticator app on first login.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Full name</Label>
            <Input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Jane Broker"
              required
              autoFocus
            />
          </div>
          <div>
            <Label>Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="jane@rivercityfinance.com.au"
              required
            />
          </div>
          <div>
            <Label>Phone (optional)</Label>
            <Input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+61 4XX XXX XXX"
            />
          </div>
          <div>
            <Label>Role</Label>
            <Select value={role} onValueChange={(v) => setRole(v as Role)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-1 text-xs text-muted-foreground">
              {ROLES.find((r) => r.value === role)?.hint}
            </p>
          </div>
          <div>
            <Label>Temporary password</Label>
            <div className="flex gap-2">
              <Input
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={12}
                className="font-mono"
              />
              <Button
                type="button"
                variant="outline"
                onClick={() => setPassword(suggestPassword())}
                title="Generate new"
              >
                🎲
              </Button>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">
              Min 12 chars. Share this securely — the user should change it after first login.
            </p>
          </div>
          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-sm text-destructive">
              {error}
            </div>
          )}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                reset()
                onClose()
              }}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Create user
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ============================================================
// Edit user dialog
// ============================================================
function EditUserDialog({
  user,
  isSelf,
  onClose,
  onSaved,
}: {
  user: AdminUser
  isSelf: boolean
  onClose: () => void
  onSaved: () => void
}) {
  const [fullName, setFullName] = useState(user.fullName)
  const [email, setEmail] = useState(user.email)
  const [phone, setPhone] = useState(user.phone ?? "")
  const [role, setRole] = useState<Role>(user.role)
  const [isActive, setIsActive] = useState(user.isActive)
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: (patch: UpdateUserInput) => usersApi.update(user.id, patch),
    onSuccess: onSaved,
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Failed to update user"),
  })

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    const patch: UpdateUserInput = {
      fullName: fullName.trim(),
      phone: phone.trim() || null,
      role,
      isActive,
    }
    // Only include email in the patch if it actually changed — avoids
    // the uniqueness check triggering on a no-op save.
    const normalisedEmail = email.trim().toLowerCase()
    if (normalisedEmail && normalisedEmail !== user.email.toLowerCase()) {
      patch.email = normalisedEmail
    }
    mutation.mutate(patch)
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit user</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Full name</Label>
            <Input value={fullName} onChange={(e) => setFullName(e.target.value)} required />
          </div>
          <div>
            <Label>Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Users log in with their email address. Changing this changes their login.
            </p>
          </div>
          <div>
            <Label>Phone</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div>
            <Label>Role</Label>
            <Select
              value={role}
              onValueChange={(v) => setRole(v as Role)}
              disabled={isSelf}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isSelf && (
              <p className="mt-1 text-xs text-muted-foreground">
                You can't change your own role. Ask another admin.
              </p>
            )}
          </div>
          <div>
            <label className="flex cursor-pointer items-center gap-2">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                disabled={isSelf}
                className="h-4 w-4"
              />
              <span className="text-sm">Active (can log in)</span>
            </label>
          </div>
          {error && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-sm text-destructive">
              {error}
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// ============================================================
// Reset password dialog
// ============================================================
function ResetPasswordDialog({
  user,
  onClose,
  onDone,
}: {
  user: AdminUser
  onClose: () => void
  onDone: (tempPassword: string) => void
}) {
  const [password, setPassword] = useState(() => suggestPassword())
  const [clearTotp, setClearTotp] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  const mutation = useMutation({
    mutationFn: () => usersApi.resetPassword(user.id, password, clearTotp),
    onSuccess: () => setDone(true),
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Failed to reset password"),
  })

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Reset password</DialogTitle>
          <DialogDescription>
            For <strong>{user.fullName}</strong> ({user.email})
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="space-y-3">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
              <Check className="mr-1 inline h-4 w-4" />
              Password reset. Share this with the user securely:
            </div>
            <CopyableField value={password} />
            <p className="text-xs text-muted-foreground">
              Ask them to sign in and change it under <em>My Settings → Change password</em>.
            </p>
            <DialogFooter>
              <Button onClick={() => onDone(password)}>Done</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label>New temporary password</Label>
              <div className="flex gap-2">
                <Input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={12}
                  className="font-mono"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPassword(suggestPassword())}
                >
                  🎲
                </Button>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">Min 12 characters.</p>
            </div>
            <label className="flex cursor-pointer items-start gap-2">
              <input
                type="checkbox"
                checked={clearTotp}
                onChange={(e) => setClearTotp(e.target.checked)}
                className="mt-0.5 h-4 w-4"
              />
              <div>
                <div className="text-sm font-medium">Also clear their 2FA</div>
                <div className="text-xs text-muted-foreground">
                  User will re-enrol their authenticator app on next login. Use this if they've lost
                  their device.
                </div>
              </div>
            </label>
            {error && (
              <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-sm text-destructive">
                {error}
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                onClick={() => mutation.mutate()}
                disabled={mutation.isPending || password.length < 12}
              >
                {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Reset password
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

// ============================================================
// Deactivate dialog
// ============================================================
function DeactivateDialog({
  user,
  onClose,
  onDone,
}: {
  user: AdminUser
  onClose: () => void
  onDone: () => void
}) {
  const [error, setError] = useState<string | null>(null)
  const mutation = useMutation({
    mutationFn: () => usersApi.deactivate(user.id),
    onSuccess: onDone,
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : "Failed to deactivate user"),
  })

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Deactivate user?</DialogTitle>
          <DialogDescription>
            <strong>{user.fullName}</strong> ({user.email}) will no longer be able to log in. Their
            records stay untouched and you can reactivate them later.
          </DialogDescription>
        </DialogHeader>
        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-2 text-sm text-destructive">
            {error}
          </div>
        )}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={mutation.isPending}
            className="bg-destructive text-white hover:bg-destructive/90"
          >
            {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Deactivate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
